import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { useId } from 'react'
import { BusinessProfileEditor } from '../BusinessProfileEditor'
import { ButtonGroup } from '@/components/ButtonGroup'
import type { BusinessProfileEditorViewModel } from '../../BusinessProfileEditorViewModel'

export interface BusinessProfileEditorDialogProps {
  vm: BusinessProfileEditorViewModel
  isDisabled?: boolean
  testId?: string
  submitButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * A dialog for viewing and editing business profile information.
 */
export const BusinessProfileEditorDialog = observer(
  function BusinessProfileEditorDialog({
    vm,
    isDisabled,
    testId,
    submitButtonTestId,
    cancelButtonTestId,
  }: BusinessProfileEditorDialogProps) {
    const formId = useId()
    const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled
    return (
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.close}
        isDismissable={!vm.save.pending && !isDisabled}
        sheetSizing="stretch"
        isUsingDialogScrolling
        size="lg"
        testId={testId}
      >
        <ModalDialogHeader>
          {vm.isReadOnly ? (
            <>
              <ModalDialogTitle>Your business</ModalDialogTitle>
              <ModalDialogDescription>
                View your business information below.
              </ModalDialogDescription>
            </>
          ) : vm.isNew ? (
            <>
              <ModalDialogTitle>Add business</ModalDialogTitle>
              <ModalDialogDescription>
                Please complete the following so we can confirm your business
                information.
              </ModalDialogDescription>
            </>
          ) : (
            <>
              <ModalDialogTitle>Edit business</ModalDialogTitle>
              <ModalDialogDescription>
                View your business profile below, and make changes if needed.
              </ModalDialogDescription>
            </>
          )}
        </ModalDialogHeader>

        <ModalDialogContent>
          <BusinessProfileEditor
            formId={formId}
            isDisabled={isFormDisabled}
            vm={vm}
          />
        </ModalDialogContent>
        <ModalDialogFooter sticky bordered compact>
          <ButtonGroup stretch>
            <Button
              isDisabled={vm.save.pending}
              variant="outline"
              label={vm.isReadOnly ? 'Close' : 'Cancel'}
              onPress={vm.close}
              testId={cancelButtonTestId}
            />
            {!vm.isReadOnly && (
              <Button
                isPending={vm.save.pending}
                isDisabled={isDisabled}
                variant="default"
                label={vm.isNew ? 'Save' : 'Save changes'}
                type="submit"
                form={formId}
                testId={submitButtonTestId}
              />
            )}
          </ButtonGroup>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
